import { z } from 'zod';
/**
 * Canonical RBAC role codes — the SINGLE SOURCE OF TRUTH shared by API, web and mobile.
 * These are the codes seeded into the database (apps/api rbac.constants ROLES); a drift-guard test
 * in the API asserts they stay in lockstep. Display labels (French UI text) live in the UI layer,
 * never here — a code is never a display string.
 *
 * Reconciled in LOT 3.5: the legacy functional-analysis names (EXPERT/ASSISTANT/CLIENT) are replaced
 * by the as-built canonical codes (EXPERT_IMMOBILIER/ASSISTANT_TECHNIQUE/CLIENT_LECTURE). No compat
 * alias is kept because no shipped consumer imported the legacy schema.
 */
export const RoleSchema = z.enum([
    'ADMIN_SYSTEME',
    'ADMIN_METIER',
    'VALIDATEUR_REFERENTIEL',
    'EXPERT_IMMOBILIER',
    'ASSISTANT_TECHNIQUE',
    'CLIENT_LECTURE',
]);
// --- DEC-005 cost catalog conceptual contracts (ADR-024) ---
/** Lifecycle status of a cost-catalog version. ACTIF is immutable. */
export const CatalogVersionStatusSchema = z.enum([
    'BROUILLON',
    'EN_VALIDATION',
    'ACTIF',
    'EXPIRE',
    'ARCHIVE',
]);
/** Explicit geographic scope of a unit cost — never inferred from NULL foreign keys. */
export const GeographicScopeTypeSchema = z.enum(['DISTRICT', 'CITY', 'ZONE_CATEGORY', 'COUNTRY']);
/** How a cost was resolved for a valuation line. FALLBACK is never silent (must be confirmed). */
export const CostResolutionModeSchema = z.enum(['EXACT_MATCH', 'FALLBACK', 'MANUAL_OVERRIDE']);
/** Variation applied when duplicating a tariff scale. */
export const TariffVariationTypeSchema = z.enum(['PERCENT', 'FIXED_AMOUNT']);
/**
 * Case (dossier) workflow statuses (BR-020). Order is significant. Canonical wire codes shared by
 * API/web/mobile — a drift-guard test keeps them equal to the Prisma `CaseStatus` enum.
 * (Renamed from `DossierStatusSchema` in LOT 3.5 to match the as-built `CaseStatus`; values unchanged.)
 */
export const CaseStatusSchema = z.enum([
    'CREE',
    'EN_COURS',
    'DONNEES_COLLECTEES',
    'CALCULE',
    'GENERE',
    'VERIFIE',
    'FINALISE',
    'ARCHIVE',
]);
/**
 * Production scenarios of a case (REQ-F-014). Canonical French codes matching the as-built Prisma
 * `CaseScenario` enum and DATA_MODEL. Reconciled in LOT 3.5 (was PV_ONLY/RAPPORT_ONLY/PV_AND_RAPPORT).
 */
export const CaseScenarioSchema = z.enum(['PV_SEUL', 'RAPPORT_SEUL', 'PV_RAPPORT']);
/**
 * LOT 9A — the three business variants of a Procès-Verbal (PV_VARIANTS_DESIGN.md).
 *
 * The variant is an EXPLICIT, persisted business attribute — never inferred. The two reference
 * documents prove it cannot be: they carry the SAME title ("PROCES VERBAL DE CONSTAT DE MISE EN
 * VALEUR AVEC EXPERTISE EN EVALUATION IMMOBILIERE") while publishing values of different natures.
 * The title is the cabinet's stationery, not a discriminator.
 */
export const PvVariantSchema = z.enum([
    'PV_MISE_EN_VALEUR',
    'PV_VALEUR_REELLE',
    'PV_VALEUR_VENALE',
]);
/**
 * LOT 9A — the named rule that determines the published value of a PV variant.
 *
 * Names what D-6 already resolves, so the rule is auditable instead of implicit:
 *  - NET_INVESTMENTS_VALUE               -> COUT.batiArrondie      (terrain excluded)
 *  - RAW_LAND_VALUE_PLUS_NET_INVESTMENTS -> reference land + net investments (LOT 9C/9D)
 *  - EXISTING_MARKET_VALUE_POLICY        -> MOYENNE.roundedValue
 */
export const PublishedValueRuleSchema = z.enum([
    'NET_INVESTMENTS_VALUE',
    'RAW_LAND_VALUE_PLUS_NET_INVESTMENTS',
    'EXISTING_MARKET_VALUE_POLICY',
]);
/**
 * LOT 9C (ARB-1) — the AUTHORISED sources of a reference land unit cost. CLOSED list.
 *
 * The real-property value deliberately ignores the market: no listing or transaction datum is
 * admissible. Making the list closed turns that exclusion into a STRUCTURAL property rather than a
 * declaration — a market price has literally no label to sit under.
 *
 * NOT to be confused with `MarketDataSource` below: `DGI_FISCAL` there is a narrative OBSERVATION
 * attached to a case, not a versioned, four-eyes-validated referential. No bridge between the two.
 */
export const LandCostSourceTypeSchema = z.enum([
    'REFERENCE_ADMINISTRATIVE',
    'BAREME_OFFICIEL',
    'REFERENCE_CADASTRALE_FISCALE',
    'REFERENTIEL_PROFESSIONNEL_DERLIZ',
]);
/** LOT 8C — the four manual market-data sources (REQ-F-061). Shared API↔Web business code. */
export const MarketDataSourceSchema = z.enum([
    'DGI_FISCAL',
    'AGENCE_IMMOBILIERE',
    'ENQUETE_TERRAIN',
    'BASE_INTERNE',
]);
/** Kind of principal (commanditaire) — matches the Prisma `MandantType` enum. */
export const MandantTypeSchema = z.enum([
    'PARTICULIER',
    'BANQUE',
    'ASSURANCE',
    'ADMINISTRATION',
    'AUTRE',
]);
/** Structure hierarchy: Bâtiment → Niveau → Espace. */
export const EspaceSchema = z.object({
    id: z.string().uuid(),
    designation: z.string().min(1),
    descriptionKeywords: z.array(z.string()).default([]),
    longueur: z.number().nonnegative().optional(),
    largeur: z.number().nonnegative().optional(),
    hauteur: z.number().nonnegative().optional(),
});
/** Health endpoint contract (used by the skeleton smoke test & Docker healthcheck). */
export const HealthResponseSchema = z.object({
    status: z.literal('ok'),
    service: z.string(),
    version: z.string(),
    timestamp: z.string(),
});
/** Currency is always FCFA / XOF for DERLIZ. */
export const CurrencySchema = z.literal('XOF');
// --- DEC-002 case & document numbering ---
/** Technical case number: `{PREFIX}-{YEAR}-{6-digit sequence}`, e.g. DERLIZ-2026-000123. */
export const CaseNumberSchema = z.string().regex(/^[A-Z][A-Z0-9]*-\d{4}-\d{6}$/);
/** Document suffix derived from the production scenario / deliverable. */
export const DocumentSuffixSchema = z.enum(['PV', 'RAP', 'PV-RAP']);
/** Build a case number from its parts (server-side allocation of the sequence). */
export function formatCaseNumber(prefix, year, sequence) {
    return `${prefix}-${year}-${String(sequence).padStart(6, '0')}`;
}
/** Derive a machine document code, e.g. DERLIZ-2026-000123-PV-v1. */
export function formatDocumentCode(caseNumber, suffix, version) {
    return `${caseNumber}-${suffix}-v${version}`;
}
// --- DEC-007 quantity formula model ---
export const QuantityInputModeSchema = z.enum(['STRUCTURED_FORMULA', 'DIRECT_QUANTITY']);
/** Measurement units for quantities (m², linear metre, unit, m³). Quantities are never rounded. */
export const MeasurementUnitSchema = z.enum(['M2', 'ML', 'U', 'M3']);
/** Lifecycle of a versioned quantity formula (ADR-027, four-eyes). ACTIF is immutable. */
export const FormulaStatusSchema = z.enum([
    'BROUILLON',
    'EN_VALIDATION',
    'ACTIF',
    'REJETE',
    'ARCHIVE',
]);
// --- LOT 4 property structure hierarchy (Bien → Structure → Niveau → Element) ---
/** Kind of property (bien) held in a dossier. */
export const BienTypeSchema = z.enum(['TERRAIN_NU', 'TERRAIN_BATI', 'IMMEUBLE', 'AUTRE']);
/** Kind of built structure (bâtiment). */
export const StructureKindSchema = z.enum(['PRINCIPAL', 'ANNEXE']);
/** Kind of valued element: interior space (needs a niveau), exterior work or added value. */
export const ElementKindSchema = z.enum(['ESPACE', 'OUVRAGE', 'PLUS_VALUE']);
/** Controlled, versioned quantity formula codes. No arbitrary executable expression is ever allowed. */
export const QuantityFormulaCodeSchema = z.enum([
    'DIRECT_QUANTITY',
    'LENGTH_X_WIDTH',
    'PERIMETER_X_HEIGHT',
    'VOLUME',
    'REPEAT_QUANTITY',
    'SUM_OF_VALUES',
    'SUM_OF_RECTANGLES',
    'PERIMETER_MINUS_OPENINGS',
    'COMPOSED_APPROVED_FORMULA',
]);
// --- DEC-008 value levels & presentation policy ---
export const ValueLevelSchema = z.enum([
    'RAW_VALUE',
    'CALCULATED_VALUE',
    'ROUNDED_VALUE',
    'PUBLISHED_VALUE',
]);
export const PresentationContextSchema = z.enum([
    'OFFICIAL_DOCUMENT',
    'TECHNICAL_SCREEN',
    'TABLE_VIEW',
    'EXPORT',
    'MOBILE',
]);
// --- DEC-009 PDF/A archival ---
export const PdfaStatusSchema = z.enum([
    'NOT_GENERATED',
    'GENERATED',
    'VALIDATION_FAILED',
    'VALID',
    'ARCHIVED',
]);
/** Signature status — qualified signature is out of MVP scope (PAdES = future). */
export const SignatureStatusSchema = z.enum(['NONE', 'SIMPLE', 'QUALIFIED']);
// --- LOT 6B/7 shared enums (single source of truth; kept in lockstep with the Prisma enums by the
//     contracts-alignment drift guard). These previously existed only inside apps/api, which meant a
//     client importing @derliz/contracts had no schema for the tokens the API actually emits (L7C-
//     CONTRACT-002/003/004). ---
/** Fine-grained document type (the tokens the API/DB actually emit). The manifest keeps a COARSE
 *  PV/RAPPORT projection (see ArchivalManifestSchema) — the two are intentionally distinct. */
export const DocumentTypeSchema = z.enum([
    'PV_BATI',
    /** LOT 9D — valeur réelle immobilière : terrain de référence + coût à neuf − vétusté. */
    'PV_VALEUR_REELLE',
    'PV_EXPERTISE_COMPLETE',
    'RAPPORT_EXPERTISE',
    'DOCUMENT_METHODE_SPECIFIQUE',
]);
/** The four MVP evaluation methods (D10). Single source shared by calc-engine, API and DB. */
export const CalcMethodCodeSchema = z.enum(['COUT', 'COMPARAISON', 'CAPITALISATION', 'MOYENNE']);
export const CalcRunStatusSchema = z.enum([
    'BROUILLON',
    'PREPARE',
    'BLOQUE',
    'CALCULE',
    'EN_REVUE',
    'VALIDE',
    'FINALISE',
    'REMPLACE',
    'ANNULE',
]);
export const DocumentStatusSchema = z.enum([
    'BROUILLON',
    'DOCX_GENERATED',
    // LOT 10F (C1) : artefact invalidé par un effacement RGPD d'une preuve incorporée ⇒ régénération imposée.
    'REGENERATION_REQUIRED',
    'PDFA_VALIDATION_PENDING',
    'PDFA_VALID',
    'PDFA_VALIDATION_FAILED',
    'FINALISE',
    'ARCHIVE',
    'REMPLACE',
    'ANNULE',
]);
export const TemplateVersionStatusSchema = z.enum(['BROUILLON', 'ACTIF', 'ARCHIVE']);
export const DocumentArtifactKindSchema = z.enum([
    'DOCX',
    'MANIFEST',
    'PDF',
    'PDFA',
    'PDFA_REMEDIATED',
    'VERAPDF_REPORT',
    'LOG',
]);
export const GenerationRunStatusSchema = z.enum(['SUCCESS', 'BLOCKED', 'FAILED']);
export const PipelineStatusSchema = z.enum([
    'PENDING',
    'PDF_CONVERSION_STARTED',
    'PDF_GENERATED',
    'PDFA_CONVERSION_STARTED',
    'PDFA_GENERATED',
    'PDFA_VALIDATION_STARTED',
    'PDFA_VALID',
    'PDFA_VALIDATION_FAILED',
    'GHOSTSCRIPT_REMEDIATION_STARTED',
    'REMEDIATED_PDFA_GENERATED',
    'PDFA_REVALIDATION_STARTED',
    'PDFA_VALIDATION_FAILED_FINAL',
    'FAILED',
]);
/** Current archival manifest schema version. Bump on any manifest structural change.
 * v2 (LOT 7A): D-6 published-value policy + selected value/source.
 * v3 (LOT 7B): PDF/A pipeline — conversion/validation/remediation tool versions, results, durations. */
/**
 * v4 (LOT 9A): adds the optional PV variant block (`pvVariant`, `publishedValueRule`,
 * `pvVariantPolicy*`). Additive — every v3 field keeps its meaning. Stored manifests are NEVER
 * recomputed, so historical documents keep their v3 payload and their byte-identical hash (ARB-4);
 * the bump only affects manifests built from now on (DEC-009: the manifest is versioned).
 */
export const MANIFEST_SCHEMA_VERSION = 'v6';
/**
 * Deterministic archival manifest (DEC-009). Serialized canonically and hashed; immutable after
 * archiving. Optional fields cover referentials that may not apply to a given document.
 */
/**
 * LOT 10C — a FROZEN reference to a selected evidence inside the archival manifest (DEC-009 v5).
 * The manifest stores only references (id + integrity hash + presentation), NEVER the binary: a
 * proof is business data selected by the document, never owned or duplicated by it. Optional array:
 * absent (dropped by the canonical form) on documents that select no evidence, so their manifest hash
 * is byte-identical to its pre-10C shape apart from the schema-version bump.
 */
export const EvidenceManifestEntrySchema = z.object({
    evidenceId: z.string(),
    sha256: z.string().regex(/^[0-9a-f]{64}$/),
    type: z.string(),
    ordre: z.number().int().nonnegative(),
    caption: z.string().optional(),
    anchorType: z.string().optional(),
    anchorId: z.string().optional(),
});
export const ArchivalManifestSchema = z.object({
    manifestSchemaVersion: z.string(),
    caseId: z.string(),
    caseNumber: CaseNumberSchema,
    documentId: z.string(),
    documentType: z.enum(['PV', 'RAPPORT']),
    documentVersion: z.number().int().positive(),
    documentDisplayLabel: z.string(),
    templateId: z.string(),
    templateVersion: z.string(),
    calcRunId: z.string(),
    calcRunHash: z.string().regex(/^[0-9a-f]{64}$/),
    roundingPolicyId: z.string(),
    roundingPolicyVersion: z.string(),
    contributionModelId: z.string(),
    contributionModelVersion: z.string(),
    costCatalogId: z.string().optional(),
    costCatalogVersion: z.string().optional(),
    coefficientSetId: z.string().optional(),
    coefficientSetVersion: z.string().optional(),
    quantityFormulaId: z.string().optional(),
    quantityFormulaVersion: z.string().optional(),
    // D-6 (LOT 7A) — published-value policy that selected the printed value (no implicit selection).
    publishedValuePolicyId: z.string().optional(),
    publishedValuePolicyVersion: z.string().optional(),
    publishedValue: z.string().optional(),
    publishedValueSource: z.string().optional(),
    // LOT 9A — the PV business variant actually applied, and the named rule it selected. Optional:
    // absent on documents generated outside the variant flow (direct generate) and on every document
    // predating LOT 9A. Absent fields are dropped by the canonical form, so historical manifest hashes
    // stay byte-identical (ARB-4). Never inferred — see PvVariantSchema.
    pvVariant: PvVariantSchema.optional(),
    publishedValueRule: PublishedValueRuleSchema.optional(),
    pvVariantPolicyId: z.string().optional(),
    pvVariantPolicyVersion: z.string().optional(),
    generatedBy: z.string(),
    validatedBy: z.string().optional(),
    generatedAt: z.string().optional(),
    validatedAt: z.string().optional(),
    finalizedAt: z.string().optional(),
    pdfaStatus: PdfaStatusSchema,
    pdfaValidationTool: z.string().optional(),
    pdfaValidationToolVersion: z.string().optional(),
    pdfaValidationReportHash: z.string().optional(),
    pdfaSha256: z.string().optional(),
    docxSha256: z.string().optional(),
    // LOT 7B — PDF/A pipeline (all optional; populated once the chain runs). veraPDF is the sole authority.
    pdfSha256: z.string().optional(),
    veraPdfProfile: z.string().optional(),
    veraPdfResult: z.boolean().optional(),
    ghostscriptUsed: z.boolean().optional(),
    ghostscriptVersion: z.string().optional(),
    conversionToolVersion: z.string().optional(),
    conversionDurationMs: z.number().int().nonnegative().optional(),
    validationDurationMs: z.number().int().nonnegative().optional(),
    archivalPipelineVersion: z.string().optional(),
    // LOT 10C — evidence SELECTED by this document, frozen at finalization. Optional & dropped when
    // empty by the canonical form, so a document without evidence keeps its historical manifest shape.
    evidence: z.array(EvidenceManifestEntrySchema).optional(),
    // A-1 — configurable photo requirement, RESOLVED for this document type and SEALED at generation.
    // Optional & dropped when absent (documents predating the feature keep their historical hash). The
    // finalization re-check reads THESE sealed values, never the live policy (no retroactive change).
    photoPolicyId: z.string().optional(),
    photoPolicyVersion: z.number().int().positive().optional(),
    photosRequired: z.boolean().optional(),
    minimumPhotoCount: z.number().int().nonnegative().optional(),
    photoCount: z.number().int().nonnegative().optional(),
    signatureStatus: SignatureStatusSchema,
});
//# sourceMappingURL=index.js.map